import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Linkedin, Mail, MessageCircle } from "lucide-react";
import { siteConfig, whatsappHref, isPlaceholder } from "@/config/siteConfig";
import { trackCtaClick } from "@/lib/analytics";

const Footer = ({ onManageCookies }) => {
  const { t } = useTranslation();

  return (
    <footer className="bg-[var(--navy)] text-white" data-testid="site-footer">
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-14 pb-14 lg:pt-16 lg:pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 leading-[1.7]">
        <div>
          <Link to="/" aria-label={`${siteConfig.COMPANY_NAME} home`} data-testid="footer-wordmark" className="inline-block">
            <img
              src={siteConfig.LOGO_INVERSE_PATH}
              alt={siteConfig.COMPANY_NAME}
              width="200"
              height="40"
              loading="lazy"
              decoding="async"
              className="h-9 w-auto block"
            />
          </Link>
          <p className="mt-4 text-[15px] text-white/70 leading-relaxed">
            {siteConfig.TAGLINE}
          </p>
          <p className="mt-4 text-[15px] text-white/85" data-testid="footer-address-city">{siteConfig.ADDRESS_CITY}</p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 mb-4">
            {t("footer.products")} · {t("footer.solutions")}
          </div>
          <ul className="space-y-2 text-[15px] leading-[1.7]">
            <li><Link className="text-white/80 hover:text-white" to="/products/blister-pack-violet-ink">{t("products.blister_pack_violet_ink")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/products/indelible-ink">{t("products.indelible_ink")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/products/porous-ink">{t("products.porous_ink")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/solutions/coding-marking">{t("solutions.coding_marking")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/industries/pharmaceutical">{t("footer.pharma_industry")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/technical-support">{t("footer.technical_support")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 mb-4">
            {t("nav.contact")}
          </div>
          <ul className="space-y-2 text-[15px] leading-[1.7]">
            <li><Link className="text-white/80 hover:text-white" to="/distributors">{t("footer.become_distributor")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/request-sample" onClick={() => trackCtaClick("footer", "request_sample")}>{t("footer.request_sample")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/global-distribution">Global Distribution</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/about">{t("footer.about")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/contact">{t("footer.contact")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 mb-4">
            Legal
          </div>
          <ul className="space-y-2 text-[15px] leading-[1.7]">
            <li><Link className="text-white/80 hover:text-white" to="/privacy-policy">{t("footer.privacy")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/terms">{t("footer.terms")}</Link></li>
            <li><Link className="text-white/80 hover:text-white" to="/cookie-policy">{t("footer.cookies")}</Link></li>
          </ul>
          <div className="mt-5 flex items-center gap-5">
            {!isPlaceholder(siteConfig.LINKEDIN_URL) && (
              <a
                href={siteConfig.LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer.linkedin")}
                className="text-white/70 hover:text-white transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-[22px] h-[22px]" />
              </a>
            )}
            <a
              href={`mailto:${siteConfig.EMAIL_SALES}`}
              aria-label={t("footer.email")}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-[22px] h-[22px]" />
            </a>
            {!isPlaceholder(siteConfig.WHATSAPP) && (
              <a
                href={whatsappHref()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer.whatsapp")}
                className="text-white/70 hover:text-white transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-[22px] h-[22px]" />
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-white/60">
            © {siteConfig.CURRENT_YEAR} {siteConfig.COMPANY_NAME}. {t("footer.rights")}
          </p>
          <button
            type="button"
            onClick={onManageCookies}
            className="text-xs text-white/70 hover:text-white underline underline-offset-4"
            data-testid="footer-manage-cookies"
          >
            {t("footer.manage_cookies")}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
